import type { IMenuProps, IOmit } from '@sixui/ui';
import { useCallback } from 'react';
import { faPen, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Menu, MenuItem } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IPoll } from '~/lib/api/modules/Polls';
import { useNavigate } from '~/components/utils/Navigation';
import { useDeletePollMutation } from '~/lib/api/modules/Polls/hooks/useDeletePollMutation';
import { useUpdatePollMutation } from '~/lib/api/modules/Polls/hooks/useUpdatePollMutation';

export type IPollListItemMenuProps = IContainerProps &
  IOmit<IMenuProps, 'trigger' | 'children'> & {
    poll: IPoll;
  };

export const PollListItemMenu: React.FC<IPollListItemMenuProps> = (props) => {
  const { sx, poll, ...other } = props;
  const { t } = useTranslation();
  const deletePollMutation = useDeletePollMutation();
  const updatePollMutation = useUpdatePollMutation();
  const navigate = useNavigate();

  const processing =
    deletePollMutation.isLoading || updatePollMutation.isLoading;

  const handleDelete = useCallback((): void => {
    if (deletePollMutation.isLoading) {
      return;
    }

    // eslint-disable-next-line no-alert
    const doDeletion = confirm(
      t('PollListItemMenu.dialog.confirmDeletion.text'),
    );

    if (doDeletion) {
      void deletePollMutation
        .mutateAsync({
          appId: poll.appId,
          pollId: poll._id,
        })
        .then(() => navigate(`/apps/${poll.appId}/polls`))
        .catch(() => {
          // TODO: handle error (snackbar)
        });
    }
  }, [deletePollMutation, poll.appId, poll._id, navigate, t]);

  return (
    <Menu
      trigger={({ getProps }) => (
        <IconButton
          icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
          loading={processing}
          {...getProps()}
        />
      )}
      placement="bottom-end"
      sx={sx}
      {...other}
    >
      <MenuItem
        label={t('common.edit')}
        leadingIcon={<FontAwesomeIcon icon={faPen} />}
        href={`/apps/${poll.appId}/polls/${poll._id}/update`}
      />
      <MenuItem
        label={t('common.delete')}
        leadingIcon={<FontAwesomeIcon icon={faTrashCan} />}
        onClick={handleDelete}
        variant="danger"
      />
    </Menu>
  );
};
